import { useEffect, useRef } from "react";
import { getImagePath } from "../getImagePath";

export const getTopicImage = (key, type = "png") =>
	encodeURI(
		process.env.PUBLIC_URL +
			getImagePath(`chart-data/chart_data_${key}.${type}`),
	);

/**
 * Break a string into an array of substrings of length up to `length`.
 * This is useful for inserting line breaks in a string.
 * @param {string} str - The string to chunk.
 * @param {number} length - The maximum length of each chunk.
 * @returns {string[]} - An array of strings, each of which is at most `length` characters long.
 */
function chunkString(str, length) {
	return str.match(new RegExp(`.{1,${length}}`, "g"));
}

const breakLineFootnote = (footnotes) => {
	const result = [];
	for (const footnote of footnotes) {
		if (footnote.length <= 58) result.push([footnote]);
		else result.push(chunkString(footnote, 58));
	}
	return result;
};

const useDownloadChartPng = (topic, resolutionScale = 2) => {
	const canvasRef = useRef(null);

	// 解析度優化設定
	const scaleFactor = resolutionScale; // 可配置的縮放倍數，預設 2x
	const baseWidth = 1080;
	const canvasWidth = baseWidth * scaleFactor;
	
	// 基礎尺寸（用於計算佈局）
	const canvasHeaderHeight = 74;
	const contentPaddingX = 56;
	const footnoteLineHeight = 28;

	useEffect(() => {
		const topicFootnote = breakLineFootnote(topic.footnote);

		const footnoteLines = topicFootnote.reduce((a, b) => a + b.length, 0);
		const drawCanvas = async () => {
			const canvas = canvasRef.current;
			if (!canvas) return;
			const ctx = canvas.getContext("2d");

			// 繪製圖表
			const chartImg = new Image();
			chartImg.crossOrigin = "anonymous";
			chartImg.src = getTopicImage(topic.key, "png"); // 改用 png 避免 svg 引用跨域資源導致 canvas.toDataURL 失敗

			chartImg.onload = async () => {
				const aspectRatio = chartImg.width / chartImg.height;
				const width = baseWidth - contentPaddingX * 2; // 使用基礎寬度計算佈局
				const chartImgHeight = width / aspectRatio;
				const sampleY = 80 + canvasHeaderHeight + 12 + chartImgHeight;
				const sampleBlockHeight =
					topic.sample.length * 31.5 + 24 - (31.5 - 18) / 2;
				const footnoteY = sampleY + 32 + sampleBlockHeight;
				const sourceTextY = footnoteY + 32 + footnoteLines * footnoteLineHeight;
				const sourceText =
					"圖片來源：國家災害防救科技中心減災動資料 0918 池上地震花東地區-主題圖表區";

				// 計算實際 Canvas 尺寸（高解析度）
				const actualCanvasWidth = canvasWidth;
				const actualCanvasHeight = (sourceTextY + 32 + 24) * scaleFactor;

				// 設定 canvas 實際尺寸（高解析度）
				canvas.width = actualCanvasWidth;
				canvas.height = actualCanvasHeight;
				
				// 設定 CSS 顯示尺寸（保持原始大小）
				canvas.style.width = `${baseWidth}px`;
				canvas.style.height = `${sourceTextY + 32 + 24}px`;

				// 縮放繪圖上下文以支援高解析度
				ctx.scale(scaleFactor, scaleFactor);

				// 繪製背景
				ctx.fillStyle = "#FFF";
				ctx.fillRect(0, 0, baseWidth, sourceTextY + 32 + 24);

				// 繪製 header 底色
				ctx.fillStyle = "#F0F0F0";
				ctx.beginPath();
				ctx.roundRect(0, 0, baseWidth, canvasHeaderHeight, [0, 0, 24, 24]);
				ctx.fill();

				// 繪製 Logo

				const logoImg = new Image();
				logoImg.crossOrigin = "anonymous";
				logoImg.src = getImagePath("/chart-data/ncdr_logo.png");
				logoImg.onload = () => {
					ctx.drawImage(logoImg, 40, 20, logoImg.width, logoImg.height);
				};

				logoImg.onerror = () => {
					console.error("ncdr logo 圖片載入失敗");
				};

				// 繪製標題
				ctx.font = "700 20px Noto Sans TC";
				ctx.fillStyle = "#373737";
				ctx.fillText(
					topic.title,
					contentPaddingX,
					canvasHeaderHeight + 40 + 24,
				);

				// 繪製圖表
				ctx.drawImage(
					chartImg,
					contentPaddingX,
					80 + canvasHeaderHeight,
					width,
					chartImgHeight,
				);

				// 繪製 sample 區塊

				ctx.fillStyle = "#F0F0F0";
				ctx.beginPath();
				ctx.roundRect(
					contentPaddingX,
					sampleY,
					baseWidth - contentPaddingX * 2,
					sampleBlockHeight,
					12,
				);
				ctx.fill();

				// 繪製 sample 文字
				topic.sample.forEach((item, index) => {
					ctx.font = "400 18px Noto Sans TC";
					ctx.fillStyle = "#373737";
					ctx.fillText(
						`\u2022 ${item}`,
						contentPaddingX + 12,
						sampleY + 30 + 31.5 * index,
					);
				});

				// 繪製 footnote 文字
				let line = 0;
				topicFootnote.forEach((footnotes, footnoteIndex) => {
					footnotes.forEach((item, lineIndex) => {
						if (lineIndex === 0) {
							ctx.font = "400 16px Noto Sans TC";
							ctx.fillStyle = "#686868";
							ctx.fillText(
								`${footnoteIndex + 1}.`,
								contentPaddingX + 12,
								footnoteY + footnoteLineHeight * line,
							);
						}
						ctx.font = "400 16px Noto Sans TC";
						ctx.fillStyle = "#686868";
						ctx.fillText(
							item,
							contentPaddingX + 12 + 20,
							footnoteY + footnoteLineHeight * line,
						);
						line++;
					});
				});

				// 繪製圖片來源文字

				ctx.font = "400 16px Noto Sans TC";
				ctx.fillStyle = "#686868";
				ctx.fillText(sourceText, contentPaddingX + 12, sourceTextY);
			};

			chartImg.onerror = () => {
				console.error("圖片載入失敗");
			};
		};

		drawCanvas();
	}, [topic, resolutionScale, canvasWidth, scaleFactor]);

	const downloadPng = () => {
		if (!canvasRef.current) return;
		const canvas = canvasRef.current;
		const dataURL = canvas.toDataURL("image/png");
		const link = document.createElement("a");
		link.download = `${topic.title}_${scaleFactor}x.png`;
		link.href = dataURL;
		link.click();
	};

	return { canvasRef, downloadPng };
};

export default useDownloadChartPng;